import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Card,
  ListGroup,
  Row,
  Container,
  Spinner,
  Table,
  Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { listSalesPrograms } from "../actions/salesProgramActions";
import CheckoutSteps from "../components/CheckoutSteps";
import { saveSalesPrograms, saveConfigure } from "../actions/cartActions";

function calculateMonthlyPayment(totalBalanceDue, annualRate, months) {
  const principal = Number(totalBalanceDue);
  const rate = Number(annualRate);
  const term = Number(months);
  if (isNaN(principal) || isNaN(rate) || isNaN(term) || term <= 0) {
    // console.error("Invalid input for payment calculation.");
    return "Invalid input";
  }
  if (rate === 0) {
    return (principal / term).toFixed(2);
  }
  const monthlyRate = rate / 100 / 12;
  const monthlyPayment =
    principal * (monthlyRate / (1 - Math.pow(1 + monthlyRate, -term)));
  return monthlyPayment.toFixed(2);
}

// SCREEN
function ConfigureScreen({ history }) {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const cartAdjustments = localStorage.getItem("adjustments")
    ? JSON.parse(localStorage.getItem("adjustments"))
    : [];

  const cartSalesProgramGroup = cartItems[0].salesProgramGroup || [];

  const salesProgramList = useSelector((state) => state.salesProgramList);
  const { loading, error, salesPrograms } = salesProgramList;

  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const handleModalClose = () => setShowModal(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [setupPDI, setSetupPDI] = useState(0);
  const [destinationCharge, setDestinationCharge] = useState(0);
  const [docFee, setDocFee] = useState(0);
  const [delivery, setDelivery] = useState(0);
  const [otherCostsDescription1, setOtherCostsDescription1] = useState("");
  const [otherCostsValue1, setOtherCostsValue1] = useState(0);
  const [otherCostsDescription2, setOtherCostsDescription2] = useState("");
  const [otherCostsValue2, setOtherCostsValue2] = useState(0);
  const [otherCostsDescription3, setOtherCostsDescription3] = useState("");
  const [otherCostsValue3, setOtherCostsValue3] = useState(0);
  const [otherCostsDescription4, setOtherCostsDescription4] = useState("");
  const [otherCostsValue4, setOtherCostsValue4] = useState(0);
  const [downPayment, setDownPayment] = useState(0);
  const [tradein, setTradein] = useState(0);
  const [months, setMonths] = useState(84);
  const [bank, setBank] = useState("");

  const [financingBestOption, setFinancingBestOption] = useState("");
  const [financingBestOptionPayment, setFinancingBestOptionPayment] =
    useState("");

  const dispatch = useDispatch();

  // CALCULATIONS
  const subtotal = cartItems.reduce((acc, item) => {
    const itemTotal = item.tractors.reduce(
      (sum, tractor) => sum + parseFloat(tractor.price),
      0
    );
    let attachmentsTotal = 0;
    let optionsTotal = 0;

    if (item.attachments) {
      attachmentsTotal = item.attachments.reduce(
        (sum, attachment) => sum + parseFloat(attachment.price),
        0
      );
    }

    if (item.options) {
      optionsTotal = item.options.reduce(
        (sum, option) => sum + parseFloat(option.price),
        0
      );
    }
    return acc + itemTotal + attachmentsTotal + optionsTotal;
  }, 0);

  const selectedProgramName = selectedPrograms.find(
    (program) => program._id === expandedProgram
  )?.name;
  const attachmentLoaderPrice = cartItems[0].attachments
    ? cartItems[0].attachments.reduce(
        (sum, attachment) =>
          sum +
          (attachment.type === "loaders" ? parseFloat(attachment.price) : 0),
        0
      )
    : 0;
  const attachmentsTotalPrice = cartItems.reduce((acc, item) => {
    const attachmentsPrice = item.attachments
      ? item.attachments.reduce(
          (sum, attachment) => sum + (parseFloat(attachment.price) || 0),
          0
        )
      : 0;
    return acc + attachmentsPrice;
  }, 0);
  const optionsTotalPrice = cartItems.reduce((acc, item) => {
    const optionsPrice = item.options
      ? item.options.reduce(
          (sum, option) => sum + (parseFloat(option.price) || 0),
          0
        )
      : 0;
    return acc + optionsPrice;
  }, 0);
  // ----------------------------Manufacturer Rebates---------------------------
  const loaderExists = cartItems[0].attachments.some(
    (attachment) => attachment.type === "loaders"
  );
  const backhoeExists = cartItems[0].attachments.some(
    (attachment) => attachment.type === "backhoes"
  );
  // added for spring program May2024
  // const midmowerExists = cartItems[0].attachments.some(
  //   (attachment) => attachment.type === "mid_mower"
  // );

  const tractorRebate =
    selectedProgramName === "Cash Rebate"
      ? cartSalesProgramGroup.op1_tractor
      : 0;

  const loaderRebate =
    selectedProgramName === "Cash Rebate" && loaderExists
      ? cartSalesProgramGroup.op1_loader
      : selectedProgramName === "Free Loader" &&
        loaderExists &&
        cartSalesProgramGroup.op5_free_loader === true
      ? attachmentLoaderPrice
      : 0;

  const otherRebate =
    selectedProgramName === "Cash Rebate" && backhoeExists
      ? cartSalesProgramGroup.op1_backhoe
      : selectedProgramName === "Low Rate + Cash"
      ? cartSalesProgramGroup.op4_rebate
      : 0;

  // -----------------------------------------------------------------------------
  const totalRebate =
    parseFloat(tractorRebate) +
    parseFloat(loaderRebate) +
    // added for spring program May2024
    // (backhoeExists ? parseFloat(cartSalesProgramGroup.op6_rebate) : 0) +
    // (midmowerExists ? parseFloat(cartSalesProgramGroup.op7_rebate) : 0) +
    parseFloat(otherRebate);

  const dealerNetAfterRebates =
    expandedProgram === null
      ? 0
      : parseFloat(subtotal) - parseFloat(totalRebate);

  const selection = cartAdjustments.selection.toLowerCase();
  const sellPriceBeforeAddedCosts =
    selection === "margin"
      ? parseFloat(dealerNetAfterRebates) / (1 - cartAdjustments.margin / 100)
      : parseFloat(dealerNetAfterRebates) * (1 + cartAdjustments.markup / 100);

  const addedCosts =
    parseFloat(otherCostsValue1 || 0) +
    parseFloat(otherCostsValue2 || 0) +
    parseFloat(otherCostsValue3 || 0) +
    parseFloat(otherCostsValue4 || 0);

  const totalOtherCosts =
    expandedProgram === null
      ? 0
      : parseFloat(setupPDI || 0) +
        parseFloat(destinationCharge || 0) +
        addedCosts;

  const SellPriceBeforeTax =
    expandedProgram === null
      ? 0
      : parseFloat(sellPriceBeforeAddedCosts) + parseFloat(totalOtherCosts);

  const summaryTax =
    expandedProgram === null
      ? 0
      : parseFloat(SellPriceBeforeTax) * (cartAdjustments.tax / 100);

  const finalPrice =
    expandedProgram === null
      ? 0
      : parseFloat(SellPriceBeforeTax) +
        parseFloat(summaryTax) +
        parseFloat(docFee || 0) +
        parseFloat(delivery || 0);

  const totalBalanceDue =
    expandedProgram === null
      ? 0
      : parseFloat(finalPrice) -
        parseFloat(downPayment || 0) -
        parseFloat(tradein || 0);

  const dealerProfit =
    expandedProgram === null
      ? 0
      : parseFloat(sellPriceBeforeAddedCosts) -
        parseFloat(dealerNetAfterRebates);

  // -------------------Dealer Discount-------------------
  const totalMSRP = cartItems.reduce((acc, item) => {
    const itemTotalMSRP = item.tractors.reduce(
      (sum, tractor) => sum + parseFloat(tractor.MSRP),
      0
    );
    let attachmentsTotalMSRP = 0;
    let optionsTotalMSRP = 0;

    if (item.attachments) {
      attachmentsTotalMSRP = item.attachments.reduce(
        (sum, attachment) => sum + parseFloat(attachment.MSRP),
        0
      );
    }

    if (item.options) {
      optionsTotalMSRP = item.options.reduce(
        (sum, option) => sum + parseFloat(option.MSRP),
        0
      );
    }
    return acc + itemTotalMSRP + attachmentsTotalMSRP + optionsTotalMSRP;
  }, 0);

  const dealerDiscount =
    parseFloat(totalMSRP) -
    parseFloat(totalRebate) -
    parseFloat(sellPriceBeforeAddedCosts);

  const totalDiscounts = parseFloat(totalRebate) + parseFloat(dealerDiscount);

  const adjustedSalesPrice =
    parseFloat(totalMSRP) -
    parseFloat(totalDiscounts) +
    parseFloat(totalOtherCosts);

  // -------------------Financing Payments-------------------
  // ACUSI
  let ACUSI_Rate;
  let ACUSI_Payment;

  if (months > 120) {
    // Adjust the terms check to the correct term limit
    ACUSI_Payment = null;
  } else {
    if (selectedProgramName === "0% Financing") {
      // Check within the specific program's terms and set rates accordingly
      if (months <= cartSalesProgramGroup.op2_terms_1) {
        ACUSI_Rate = cartSalesProgramGroup.op2_rate_1;
      } else if (months <= cartSalesProgramGroup.op2_terms_2) {
        ACUSI_Rate = cartSalesProgramGroup.op2_rate_2;
      } else if (months <= cartSalesProgramGroup.op2_terms_3) {
        ACUSI_Rate = cartSalesProgramGroup.op2_rate_3;
      } else if (months <= cartSalesProgramGroup.op2_terms_4) {
        ACUSI_Rate = cartSalesProgramGroup.op2_rate_4;
      } else {
        // If months exceed the specific program's terms, use a standard rate
        ACUSI_Rate = cartSalesProgramGroup.std_3_rate_4; // Assume this is the fallback standard rate
      }
    } else {
      // For cases where no specific program is selected or terms exceed the program's limits
      if (months <= cartSalesProgramGroup.std_3_terms_1) {
        ACUSI_Rate = cartSalesProgramGroup.std_3_rate_1;
      } else if (months <= cartSalesProgramGroup.std_3_terms_2) {
        ACUSI_Rate = cartSalesProgramGroup.std_3_rate_2;
      } else if (months <= cartSalesProgramGroup.std_3_terms_3) {
        ACUSI_Rate = cartSalesProgramGroup.std_3_rate_3;
      } else {
        ACUSI_Rate = cartSalesProgramGroup.std_3_rate_4;
      }
    }
    ACUSI_Payment = calculateMonthlyPayment(
      totalBalanceDue,
      ACUSI_Rate,
      months
    );
  }

  // WELLS FARGO
  let WellsFargo_Rate;
  let WellsFargo_Payment;

  if (months > cartSalesProgramGroup.std_1_terms_1) {
    WellsFargo_Payment = null;
  } else {
    // program op 3 - 0% financing for 72 months - wells fargo
    if (
      selectedProgramName === "0% Financing" &&
      months <= cartSalesProgramGroup.op3_terms_1
    ) {
      WellsFargo_Rate = cartSalesProgramGroup.op3_rate_1;
    } else {
      // Use standard rates if no special program fits
      WellsFargo_Rate = cartSalesProgramGroup.std_1_rate_1;
    }
    WellsFargo_Payment = calculateMonthlyPayment(
      totalBalanceDue,
      WellsFargo_Rate,
      months
    );
  }

  // DLL
  let DLL_Rate;
  let DLL_Payment;

  // Check if the selected term exceeds the maximum allowed term for DLL
  if (months > cartSalesProgramGroup.std_2_terms_1) {
    DLL_Payment = null;
  } else {
    if (selectedProgramName === "Low Rate + Cash") {
      // Use the program-specific rate if within the program's terms
      if (months <= cartSalesProgramGroup.op4_terms_1) {
        DLL_Rate = cartSalesProgramGroup.op4_rate_1;
      } else {
        // If months exceed the specific program's terms, use the standard rate
        DLL_Rate = cartSalesProgramGroup.std_2_rate_1;
      }
    } else {
      // Use the standard rate if no specific program is selected
      DLL_Rate = cartSalesProgramGroup.std_2_rate_1;
    }

    // Calculate the payment
    DLL_Payment = calculateMonthlyPayment(totalBalanceDue, DLL_Rate, months);
  }

  // SHEFFIELD
  let SHEFFIELD_Rate;
  let SHEFFIELD_Payment;

  if (months > 84) {
    // Assuming 85 is the maximum term limit for Sheffield financing
    SHEFFIELD_Payment = null;
  } else {
    // Set SHEFFIELD_Rate based on the terms range
    if (months <= cartSalesProgramGroup.std_4_terms_1) {
      SHEFFIELD_Rate = cartSalesProgramGroup.std_4_rate_1;
    } else if (months <= cartSalesProgramGroup.std_4_terms_2) {
      SHEFFIELD_Rate = cartSalesProgramGroup.std_4_rate_2;
    } else {
      // Assume a default rate if no specific term fits
      SHEFFIELD_Rate = cartSalesProgramGroup.std_4_terms_4; // Or some other default rate
    }

    SHEFFIELD_Payment = calculateMonthlyPayment(
      totalBalanceDue,
      SHEFFIELD_Rate,
      months
    );
  }

  // -------------------Financing Color-------------------
  const isLowestPayment = (bank) => {
    const payments = {
      wf: WellsFargo_Payment,
      dll: DLL_Payment,
      acusi: ACUSI_Payment,
      sheffield: SHEFFIELD_Payment,
    };
    const filteredPayments = Object.values(payments).filter(
      (payment) => payment > 0
    );
    const lowestPayment = Math.min(...filteredPayments);
    if (filteredPayments.length === 0) {
      return false;
    }
    const tolerance = 0.01;
    return (
      payments[bank] > 0 && Math.abs(payments[bank] - lowestPayment) < tolerance
    );
  };

  // --------------------Financing Totals-------------------
  const totalPayments =
    parseFloat(financingBestOptionPayment) * parseFloat(months);

  const totalInterest = parseFloat(totalPayments) - parseFloat(totalBalanceDue);

  // -------------------------------------------------------

  useEffect(() => {
    if (isLowestPayment("wf")) {
      setFinancingBestOption(
        "WF" +
          " " +
          WellsFargo_Rate +
          " " +
          "for " +
          months +
          "mo " +
          " $" +
          WellsFargo_Payment
      );
      setFinancingBestOptionPayment(WellsFargo_Payment);
    } else if (isLowestPayment("dll")) {
      setFinancingBestOption(
        "DLL" +
          " " +
          DLL_Rate +
          " " +
          "for " +
          months +
          "mo " +
          " $" +
          DLL_Payment
      );
      setFinancingBestOptionPayment(DLL_Payment);
    } else if (isLowestPayment("acusi")) {
      setFinancingBestOption(
        "ACUSI" +
          " " +
          ACUSI_Rate +
          " " +
          "for " +
          months +
          "mo " +
          " $" +
          ACUSI_Payment
      );
      setFinancingBestOptionPayment(ACUSI_Payment);
    } else if (isLowestPayment("sheffield")) {
      setFinancingBestOption(
        "SHEFFIELD" +
          " " +
          SHEFFIELD_Rate +
          " " +
          "for " +
          months +
          "mo " +
          " $" +
          SHEFFIELD_Payment
      );
      setFinancingBestOptionPayment(SHEFFIELD_Payment);
    } else {
      setFinancingBestOption("");
      setFinancingBestOptionPayment("");
    }
  }, [
    isLowestPayment,
    WellsFargo_Rate,
    DLL_Rate,
    ACUSI_Rate,
    SHEFFIELD_Rate,
    WellsFargo_Payment,
    DLL_Payment,
    ACUSI_Payment,
    SHEFFIELD_Payment,
    selectedProgramName,
    months,
  ]);

  // ----------------------------------------------------
  const t_list_description =
    cartItems[0].tractors[0].list_description +
    " " +
    cartItems[0].tractors[0].tire_description;
  const t_MSRP = cartItems[0].tractors[0].MSRP;
  // const a_list_description = cartItems[0].attachments.length > 1
  // ? cartItems[0].attachments.map((attachment) => attachment.name + " . ").join('')
  // : " ";
  const a_list_description = cartItems[0].attachments
    .map((attachment) => attachment.name + " . ")
    .join("");
  const a_qty = cartItems[0].attachments.length;
  const a_MSRP = cartItems[0].attachments.reduce(
    (sum, attachment) => sum + parseFloat(attachment.MSRP),
    0
  );
  const o_list_description = cartItems[0].options
    .map((option) => option.name + " . ")
    .join("");
  const o_qty = cartItems[0].options.length;
  const o_MSRP = cartItems[0].options.reduce(
    (sum, option) => sum + parseFloat(option.MSRP),
    0
  );
  const tax_percent = cartAdjustments.tax;

  // -----------------------------------------------------
  const selectHandler = (program) => {
    if (selectedPrograms.find((p) => p._id === program._id)) {
      setSelectedPrograms((prevPrograms) =>
        prevPrograms.filter((p) => p._id !== program._id)
      );
      if (expandedProgram === program._id) {
        setExpandedProgram(null);
      }
    } else {
      setSelectedPrograms([program]);
      setExpandedProgram(program._id);
    }
  };

  // --------------
  const handleShowModal = (program) => {
    setSelectedProgram(program);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };
  // saving the financing option text 
  let updatedFinancingBestOption = financingBestOption;
  if (bank === "Other") {
      updatedFinancingBestOption = "Other";
  } else if (bank === "") {
      // If no bank is selected, keep the existing financingBestOption
      updatedFinancingBestOption = financingBestOption;
  }
  const submitHandler = (e) => {
    e.preventDefault();
    history.push("/clientinfo");
    dispatch(saveSalesPrograms(selectedPrograms));
    dispatch(
      saveConfigure({
        t_list_description,
        t_MSRP,
        a_list_description,
        a_qty,
        a_MSRP,
        o_list_description,
        o_qty,
        o_MSRP,

        tractorRebate,
        loaderRebate,
        otherRebate,
        totalRebate,
        dealerDiscount,
        totalDiscounts,
        adjustedSalesPrice,

        setupPDI,
        destinationCharge,
        docFee,
        delivery,
        otherCostsDescription1,
        otherCostsDescription2,
        otherCostsDescription3,
        otherCostsDescription4,
        addedCosts,
        totalOtherCosts,
        downPayment,
        tradein,

        tax_percent,
        summaryTax,
        totalBalanceDue,

        // financingBestOption,
        financingBestOption: updatedFinancingBestOption,

        selectedProgramName,
        bank,
        months,
        totalPayments,
        totalInterest,
      })
    );
  };
  useEffect(() => {
    if (!userInfo) {
      history.push("/login");
    } else if (cartItems.length === 0 || cartItems.length > 1) {
      history.push("/cart");
    } else if (!cartAdjustments.selection) {
      history.push("/adjustments");
    } else {
      dispatch(listSalesPrograms());
    }
  }, [dispatch, history, userInfo, cartItems, cartAdjustments.selection]);

  return (
    <Container>
      <CheckoutSteps step1 step2 />

      <Row className="justify-content-md-center my-3">
        <h2 className="text-center">
          Elevate Your Sales with Our Exclusive Dealer Programs!
        </h2>
      </Row>
      <Row>
        {loading ? (
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        ) : error ? (
          <p>{error}</p>
        ) : (
          salesPrograms.map((program) => (
            <Col key={program._id}>
              <Card
                style={{
                  borderRadius: "20px",
                }}
              >
                <Card.Header
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    textAlign: "center",
                    borderRadius: "20px 20px 0px 0px",
                  }}
                >
                  {program.name}
                </Card.Header>

                <Card.Body className=" row ">
                  <Button
                    style={{
                      color: "gray",
                      borderColor: "gray",
                    }}
                    variant="outline-light"
                    className="buttonMoreInfo configure col-5"
                    onClick={() => handleShowModal(program)}
                  >
                    More Info
                  </Button>

                  <Button
                    variant={
                      selectedPrograms.find((p) => p._id === program._id)
                        ? "outline-danger"
                        : "outline-primary"
                    }
                    className="buttonAdd configure col-5"
                    onClick={() => selectHandler(program)}
                  >
                    {selectedPrograms.find((p) => p._id === program._id)
                      ? "Remove"
                      : "Add"}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))
        )}
      </Row>
      <hr />
      <Row>
        {/* message for dealer */}
        <Col>
          <p className="text-center smallDescription">
            Here, you'll see the sales programs you chose for your quote. If you
            want to change your selection, just click the "Add" or "Remove"
            buttons on each program card. Remember, you can only have one
            program per quote.
            {/* *** Please note, proceeding to the next page requires the selection of at least one program and a financing bank. *** */}
          </p>
        </Col>
      </Row>

      {/* ACCORDION PROGRAMS SECTION*/}
      <Row>
        <Col md={8}>
          <h4 className="summaryTitle">Sales Programs:</h4>
          {selectedPrograms.map((program, index) => (
            <div
              className="accordion"
              id={`accordion-${program._id}`}
              key={program._id}
            >
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${
                      expandedProgram !== program._id ? "collapsed" : ""
                    }`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-${program._id}`}
                    aria-expanded={expandedProgram === program._id}
                    aria-controls={`collapse-${program._id}`}
                    onClick={() =>
                      setExpandedProgram(
                        expandedProgram === program._id ? null : program._id
                      )
                    }
                  >
                    {program.name}
                  </button>
                </h2>
                <div
                  id={`collapse-${program._id}`}
                  className={`accordion-collapse collapse ${
                    expandedProgram === program._id ? "show" : ""
                  }`}
                  data-bs-parent={`#accordion-${program._id}`}
                >
                  <div className="accordion-body">
                    {/* add Other costs table */}
                    <Table className="table table-hover">
                      <thead>
                        <tr>
                          <th className="p-2 w-100">Other Costs</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="table-primary">
                          <td className="label3">Set up & PDI</td>
                          <td>
                            <input
                              className="input dollar3"
                              type="number"
                              step="0.01"
                              min="0"
                              onChange={(e) =>
                                setSetupPDI(Math.max(0, e.target.value))
                              }
                            />
                          </td>
                        </tr>
                        <tr className="table-primary">
                          <td className="label3">Destination Charge</td>
                          <td>
                            <input
                              className="input dollar3"
                              type="number"
                              step="0.01"
                              min="0"
                              onChange={(e) =>
                                setDestinationCharge(e.target.value)
                              }
                            />
                          </td>
                        </tr>

                        <tr className="">
                          <td className="label3">
                            <input
                              type="text"
                              className="rounded"
                              maxLength={11}
                              placeholder="e.g. Other costs"
                              onChange={(e) =>
                                setOtherCostsDescription1(e.target.value)
                              }
                              required={!!otherCostsValue1}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input dollar3"
                              step="0.01"
                              min="0"
                              onChange={(e) =>
                                setOtherCostsValue1(e.target.value)
                              }
                            />
                          </td>
                        </tr>
                        <tr className="">
                          <td className="label3">
                            <input
                              type="text"
                              maxLength={11}
                              className="rounded"
                              onChange={(e) =>
                                setOtherCostsDescription2(e.target.value)
                              }
                              required={!!otherCostsValue2}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input dollar3"
                              step="0.01"
                              min={0}
                              onChange={(e) =>
                                setOtherCostsValue2(e.target.value)
                              }
                            />
                          </td>
                        </tr>
                        <tr className="table-primary">
                          <td className="label3">Proc/Doc. Fee</td>
                          <td>
                            <input
                              className="input dollar3"
                              type="number"
                              step="0.01"
                              min={0}
                              onChange={(e) => setDocFee(e.target.value)}
                            />
                          </td>
                        </tr>
                        <tr className="table-primary">
                          <td className="label3">Delivery</td>
                          <td>
                            <input
                              className="input dollar3"
                              type="number"
                              step="0.01"
                              min={0}
                              onChange={(e) => setDelivery(e.target.value)}
                            />
                          </td>
                        </tr>
                        <tr className="">
                          <td className="label3">
                            <input
                              type="text"
                              className="rounded"
                              maxLength={11}
                              placeholder="e.g. Other costs"
                              onChange={(e) =>
                                setOtherCostsDescription3(e.target.value)
                              }
                              required={!!otherCostsValue3}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input dollar3"
                              step="0.01"
                              min={0}
                              onChange={(e) =>
                                setOtherCostsValue3(e.target.value)
                              }
                            />
                          </td>
                        </tr>
                        <tr className="">
                          <td className="label3">
                            <input
                              type="text"
                              maxLength={11}
                              className="rounded"
                              onChange={(e) =>
                                setOtherCostsDescription4(e.target.value)
                              }
                              required={!!otherCostsValue4}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input dollar3"
                              step="0.01"
                              min={0}
                              onChange={(e) =>
                                setOtherCostsValue4(e.target.value)
                              }
                            />
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* 4 column table for Financing payments info */}
                    <Table className="table table-hover table-warning">
                      <thead>
                        <tr>
                          <th>Financing Options</th>
                          <th>Term</th>
                          <th>Rate</th>
                          <th>Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          className={
                            isLowestPayment("wf")
                              ? "table-success attCartPrice"
                              : ""
                          }
                        >
                          <td className="label3">Wells Fargo</td>
                          <td>{months}</td>
                          <td>{WellsFargo_Rate || "0.0"}%</td>
                          <td>
                            $
                            {parseFloat(WellsFargo_Payment)
                              ? parseFloat(WellsFargo_Payment).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )
                              : "0.00"}
                          </td>
                        </tr>
                        <tr
                          className={
                            isLowestPayment("dll")
                              ? "table-success attCartPrice"
                              : ""
                          }
                        >
                          <td className="label3">DLL</td>
                          <td>{months}</td>
                          <td>{DLL_Rate || "0.0"}%</td>
                          <td>
                            $
                            {parseFloat(DLL_Payment)
                              ? parseFloat(DLL_Payment).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )
                              : "0.00"}
                          </td>
                        </tr>
                        <tr
                          className={
                            isLowestPayment("acusi")
                              ? "table-success attCartPrice"
                              : ""
                          }
                        >
                          <td className="label3">ACUSI</td>
                          <td>{months}</td>
                          <td>{ACUSI_Rate || "0.0"}%</td>
                          <td>
                            $
                            {parseFloat(ACUSI_Payment)
                              ? parseFloat(ACUSI_Payment).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )
                              : "0.00"}
                          </td>
                        </tr>
                        <tr
                          className={
                            isLowestPayment("sheffield")
                              ? "table-success attCartPrice"
                              : ""
                          }
                        >
                          <td className="label3">SHEFFIELD</td>
                          <td>{months}</td>
                          <td>{SHEFFIELD_Rate || "0.0"}%</td>
                          <td>
                            $
                            {parseFloat(SHEFFIELD_Payment)
                              ? parseFloat(SHEFFIELD_Payment).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )
                              : "0.00"}
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    <div className="row">
                      {/* input for trade in */}
                      <div className="col">
                        <div className="form-group">
                          <label className="label">Trade In:</label>
                          <input
                            type="number"
                            className="input dollar4"
                            step="0.01"
                            min={0}
                            placeholder="Trade In"
                            onChange={(e) => setTradein(e.target.value)}
                          />
                        </div>
                      </div>

                      {/* input to Down payment */}
                      <div className="col">
                        <div className="form-group">
                          <label className="label">Down Payment:</label>
                          <input
                            type="number"
                            className="input dollar4 "
                            step="0.01"
                            min={0}
                            placeholder="Down Payment"
                            onChange={(e) => setDownPayment(e.target.value)}
                          />
                        </div>
                      </div>

                      {/* select months */}
                      <div className="col ">
                        <div className="form-group">
                          <label className="label">Months:</label>
                          <select
                            className="rounded form-select"
                            onChange={(e) => setMonths(e.target.value)}
                            defaultValue={"84"}
                          >
                            <option value="12">1 Year | 12 mo</option>
                            <option value="24">2 Years | 24 mo</option>
                            <option value="36">3 Years | 36 mo</option>
                            <option value="48">4 Years | 48 mo</option>
                            <option value="60">5 Years | 60 mo</option>
                            <option value="72">6 Years | 72 mo</option>
                            <option value="84">7 Years | 84 mo</option>
                            <option value="96">8 Years | 96 mo</option>
                            <option value="108">9 Years | 108 mo</option>
                            <option value="120">10 Years | 120 mo</option>
                          </select>
                        </div>
                      </div>

                      {/* select bank */}
                      <div className="col ms-0 me-0">
                        <div className="form-group ">
                          <label className="label">Financing: </label>
                          <select
                            className="rounded form-select"
                            onChange={(e) => setBank(e.target.value)}
                          >
                            <option value="">-- Select --</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <br />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* --------------------- */}
        </Col>
        {/* SUMMARY SECTION*/}
        <Col md={4} className="order-md-2 mb-4">
          <h4 className="summaryTitle">
            Summary:{" "}
            {expandedProgram ? (
              <span style={{ color: "#007bff" }}>
                {
                  selectedPrograms.find(
                    (program) => program._id === expandedProgram
                  )?.name
                }
              </span>
            ) : (
              ""
            )}
          </h4>
          <hr></hr>
          <span className="cardTextProfit">Dealer Profit: </span>
          <br />
          <span className="cardTextProfit price3">
            $
            {parseFloat(dealerProfit)
              ? parseFloat(dealerProfit).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "0.00"}
          </span>

          <ListGroup className="mb-3">
            <ListGroup.Item
              style={{
                borderRadius: "10px 10px 0px 0px",
              }}
            >
              <Row>
                <Col>Tractor:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(
                      cartItems.reduce((acc, item) => acc + item.qty, 0) *
                        cartItems.reduce(
                          (acc, item) =>
                            acc +
                            item.tractors.reduce(
                              (acc, tractor) => acc + parseFloat(tractor.price),
                              0
                            ),
                          0
                        )
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            {userInfo ? (
              <ListGroup.Item>
                <Row>
                  <Col>Attachments:</Col>
                  <Col>
                    <span className="price style1">
                      $
                      {attachmentsTotalPrice
                        ? attachmentsTotalPrice.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "0.00"}
                    </span>
                  </Col>
                </Row>
              </ListGroup.Item>
            ) : (
              <span></span>
            )}
            {userInfo ? (
              <ListGroup.Item>
                <Row>
                  <Col>Options:</Col>
                  <Col>
                    <span className="price style1">
                      $
                      {optionsTotalPrice
                        ? optionsTotalPrice.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "0.00"}
                    </span>
                  </Col>
                </Row>
              </ListGroup.Item>
            ) : (
              <span></span>
            )}
            {userInfo ? (
              <ListGroup.Item>
                <Row>
                  <Col>Total Net:</Col>
                  <Col>
                    <span className="price style1">
                      $
                      {subtotal
                        ? subtotal.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "0.00"}
                    </span>
                  </Col>
                </Row>
              </ListGroup.Item>
            ) : (
              <span></span>
            )}
            <Col
              style={{
                fontWeight: "bold",
                backgroundColor: "#f0f0f0",
                color: "#999999",
                padding: "0.2rem",
                textAlign: "center",
              }}
            >
              Manufacturer Rebates:
            </Col>

            <ListGroup.Item>
              <Row>
                <Col>Tractor Rebate:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(tractorRebate)
                      ? parseFloat(tractorRebate).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>

            <ListGroup.Item>
              <Row>
                <Col>Loader Rebate:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(loaderRebate)
                      ? parseFloat(loaderRebate).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Other Rebate:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(otherRebate)
                      ? parseFloat(otherRebate).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            {/* ADDED FOR SPRING PROGRAM 
            {backhoeExists && (
              <ListGroup.Item>
              <Row
              style={{
                backgroundColor: "#feffad",
                borderRadius: "10px",
              }}
              >
                <Col>{
                  cartItems[0].salesProgramGroup.op6_description
                }:</Col>
                <Col>
                
                  <span className="price style1" >
                    $
                    {parseFloat(cartSalesProgramGroup.op6_rebate)
                      ? parseFloat(cartSalesProgramGroup.op6_rebate).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            )}
            {midmowerExists && (
              <ListGroup.Item>
              <Row
              style={{
                backgroundColor: "#feffad",
                borderRadius: "10px",
              }}
              >
                <Col>{
                  cartItems[0].salesProgramGroup.op7_description
                }:</Col>
                <Col>
                
                  <span className="price style1">
                    $
                    {parseFloat(cartSalesProgramGroup.op7_rebate)
                      ? parseFloat(cartSalesProgramGroup.op7_rebate).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            )}
            
             END OF ADDED FOR SPRING PROGRAM */}
            <ListGroup.Item>
              <Row
                style={{
                  backgroundColor: "#c9e2fd",
                  borderRadius: "10px",
                }}
              >
                <Col>Total Rebate:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(totalRebate)
                      ? parseFloat(totalRebate).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row
                style={{
                  backgroundColor: "#e9efec",
                  borderRadius: "10px",
                }}
              >
                <Col>Dealer Net aft. Rebates:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(dealerNetAfterRebates)
                      ? parseFloat(dealerNetAfterRebates).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>
                  Dealer Gain (
                  <strong>
                    {cartAdjustments.selection === "markup"
                      ? "Markup"
                      : "Margin"}
                  </strong>
                  ):
                </Col>
                <Col>
                  <span className="price style1">
                    {cartAdjustments.selection === "markup"
                      ? cartAdjustments.markup
                      : cartAdjustments.margin}
                    %
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Sell Price bf Add Costs:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(sellPriceBeforeAddedCosts)
                      ? parseFloat(sellPriceBeforeAddedCosts).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>

            <Col
              style={{
                fontWeight: "bold",
                backgroundColor: "#f0f0f0",
                color: "#999999",
                padding: "0.2rem",
                textAlign: "center",
              }}
            >
              Other Costs after Margin Calculation:
            </Col>
            <ListGroup.Item>
              <Row>
                <Col>Set up & PDI:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {setupPDI
                      ? setupPDI.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Destination Charge:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(destinationCharge)
                      ? parseFloat(destinationCharge).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Added Costs:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(addedCosts)
                      ? parseFloat(addedCosts).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>

            <ListGroup.Item>
              <Row>
                <Col>Total Other Costs:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(totalOtherCosts)
                      ? parseFloat(totalOtherCosts).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row
                style={{
                  backgroundColor: "#feffad",
                  borderRadius: "10px",
                }}
              >
                <Col>Sell Price before Tax:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(SellPriceBeforeTax)
                      ? parseFloat(SellPriceBeforeTax).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>
                  State Tax: (
                  <span className="price">
                    <strong>{cartAdjustments.tax}</strong>
                  </span>
                  %)
                </Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(summaryTax)
                      ? parseFloat(summaryTax).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Proc/Doc. Fee:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(docFee)
                      ? parseFloat(docFee).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Delivery:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(delivery)
                      ? parseFloat(delivery).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row
                style={{
                  backgroundColor: "#eaeaea",
                  borderRadius: "10px",
                }}
              >
                <Col>Final Price w Tax & Fees:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(finalPrice)
                      ? parseFloat(finalPrice).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Customer Down Payment:</Col>
                <Col>
                  <span className="price style1">
                    $
                    {parseFloat(downPayment)
                      ? parseFloat(downPayment).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Trade In:</Col>
                <Col>
                  <span className="price style1">
                    ${/* TRADE IN */}
                    {parseFloat(tradein)
                      ? parseFloat(tradein).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item
              style={{
                borderRadius: "0px 0px 10px 10px",
              }}
            >
              <Row
                style={{
                  backgroundColor: "#aef9b5",
                  borderRadius: "10px",
                }}
              >
                <Col
                  style={{
                    paddingTop: "0.5rem",
                    paddingBottom: "0.5rem",
                  }}
                >
                  <strong>Total Balance Due:</strong>
                </Col>
                <Col>
                  <span className="price style2">
                    $
                    {parseFloat(totalBalanceDue)
                      ? parseFloat(totalBalanceDue).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0.00"}
                  </span>
                </Col>
              </Row>
            </ListGroup.Item>
          </ListGroup>
          <div className="py-5 text-center">
            <Button
              className="button adjust"
              type="submit"
              variant="primary"
              disabled={selectedPrograms.length === 0}
              onClick={submitHandler}
            >
              Continue
            </Button>
          </div>
        </Col>
      </Row>
      {/* More Info Modal */}

      <Modal
        className="modal modal-dialog-centered modal-dialog</Card>-scrollable roundedModalContent"
        tabIndex="-1"
        show={showModal}
        onHide={handleModalClose}
      >
        <Modal.Header className="modal-header">
          <p className="modal-title label">
            <strong>{selectedProgram?.short_description}</strong>
          </p>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={handleCloseModal}
          ></button>
        </Modal.Header>
        <Modal.Body className="modal-body">
          <p
            style={{
              fontSize: "1.2rem",
            }}
          >
            {selectedProgram?.description}
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-footer"></Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ConfigureScreen;
